import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowRight, Key, Shield, Users, Trash2, Edit, Package, Building2, ClipboardList, CreditCard, Download, Upload, Cloud, Clock, Bell, Mail, Send, Lock, RotateCcw, Trash, Monitor, Smartphone, LogOut, AlertTriangle, Check, StickyNote, RotateCw, Calendar } from 'lucide-react';
import { toast } from '../hooks/use-toast';

import { LockoutsPanel } from '../components/LockoutsPanel';
import { VatSettings } from '../components/VatSettings';
import { 
  getSuppliers, addSupplier, deleteSupplier, 
  getBranches, addBranch, deleteBranch,
  getExecutionSteps, addExecutionStep, deleteExecutionStep,
  getPaymentMethods, addPaymentMethod, deletePaymentMethod,
  getBankAccounts, addBankAccount, deleteBankAccount,
  syncAllDailyTasksToCalendar,
  syncDailyTasksFromCalendar,
  downloadBackup,
  restoreBackup,
  connectGoogleDrive,
  getDriveStatus,
  backupToDrive,
  getBackupSchedule,
  setBackupSchedule,
  getNotificationSettings,
  updateNotificationSettings,
  sendTestEmail,
  getAllowedEmails,
  addAllowedEmail,
  deleteAllowedEmail,
  getRecycleBin,
  restoreFromRecycleBin,
  permanentlyDeleteFromRecycleBin,
  emptyRecycleBin,
  getLoginSessions,
  disconnectSession,
  disconnectAllSessions,
  getSuspiciousLogins,
  dismissSuspiciousLogin,
  clearSuspiciousLogins,
  getSecurityAlertSettings,
  updateSecurityAlertSettings,
  getCompletedNotes,
  updateNote,
  deleteNote,
  getGoogleCalendarStatus,
  disconnectGoogleCalendar,
  syncTasksToGoogleCalendar,
  pullEventsFromGoogleCalendar,
  getGoogleCalendarAuthUrl
} from '../api';

const Manager = () => {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Admin password change
  const [currentAdminPassword, setCurrentAdminPassword] = useState('');
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [confirmAdminPassword, setConfirmAdminPassword] = useState('');
  const [isSubmittingAdmin, setIsSubmittingAdmin] = useState(false);
  
  // AIMAN folder password change
  const [currentAimanPassword, setCurrentAimanPassword] = useState('');
  const [newAimanPassword, setNewAimanPassword] = useState('');
  const [confirmAimanPassword, setConfirmAimanPassword] = useState('');
  const [isSubmittingAiman, setIsSubmittingAiman] = useState(false);
  
  // Manager management state
  const [managers, setManagers] = useState([]);
  const [newManagerName, setNewManagerName] = useState('');
  const [newManagerPassword, setNewManagerPassword] = useState('');
  const [isAddingManager, setIsAddingManager] = useState(false);
  const [editingManager, setEditingManager] = useState(null);
  const [editPassword, setEditPassword] = useState('');

  // Supplier management state
  const [suppliers, setSuppliers] = useState([]);
  const [newSupplierName, setNewSupplierName] = useState('');
  const [isAddingSupplier, setIsAddingSupplier] = useState(false);

  // Branch management state
  const [branches, setBranches] = useState([]);
  const [newBranchName, setNewBranchName] = useState('');
  const [isAddingBranch, setIsAddingBranch] = useState(false);

  // Execution steps management state
  const [executionSteps, setExecutionSteps] = useState([]);
  const [newStepName, setNewStepName] = useState('');
  const [isAddingStep, setIsAddingStep] = useState(false);

  // Payment methods management state
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [newPaymentName, setNewPaymentName] = useState('');
  const [isAddingPayment, setIsAddingPayment] = useState(false);

  // Bank accounts management state
  const [bankAccounts, setBankAccounts] = useState([]);
  const [newBankAccount, setNewBankAccount] = useState({ bankName: '', branch: '', accountNumber: '' });
  const [isAddingBankAccount, setIsAddingBankAccount] = useState(false);

  // Backup state
  const [isRestoring, setIsRestoring] = useState(false);
  const [isDriveConnected, setIsDriveConnected] = useState(false);
  const [isBackingUpToDrive, setIsBackingUpToDrive] = useState(false);
  const [scheduleDay, setScheduleDay] = useState('daily');
  const [scheduleHour, setScheduleHour] = useState('02');
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [isSavingSchedule, setIsSavingSchedule] = useState(false);
  const fileInputRef = React.useRef(null);

  // Notification settings state
  const [notifEnabled, setNotifEnabled] = useState(false);
  const [notifEmail, setNotifEmail] = useState('');
  const [notifThreshold, setNotifThreshold] = useState('0');
  const [isSavingNotif, setIsSavingNotif] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);

  // Allowed emails state
  const [allowedEmails, setAllowedEmails] = useState([]);
  const [newAllowedEmail, setNewAllowedEmail] = useState('');
  const [isAddingEmail, setIsAddingEmail] = useState(false);

  // Recycle bin state
  const [recycleBinItems, setRecycleBinItems] = useState([]);
  const [recycleBinFilter, setRecycleBinFilter] = useState('');
  const [isLoadingRecycleBin, setIsLoadingRecycleBin] = useState(false);
  const [recycleBinOpen, setRecycleBinOpen] = useState(false);

  // Active sessions state
  const [activeSessions, setActiveSessions] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const currentSessionId = localStorage.getItem('sessionId') || '';

  // Suspicious logins state
  const [suspiciousLogins, setSuspiciousLogins] = useState([]);

  // Security alert settings state
  const [alertEmail, setAlertEmail] = useState('');
  const [alertPhone, setAlertPhone] = useState('');
  const [isSavingAlertSettings, setIsSavingAlertSettings] = useState(false);

  // Notes history state
  const [completedNotes, setCompletedNotes] = useState([]);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [gcalStatus, setGcalStatus] = useState({ connected: false, email: '' });
  const [gcalLoading, setGcalLoading] = useState(false);

  // Load Drive status and schedule on mount
  useEffect(() => {
    const loadBackupSettings = async () => {
      try {
        const driveStatus = await getDriveStatus();
        setIsDriveConnected(driveStatus.connected);
        
        const schedule = await getBackupSchedule();
        if (schedule) {
          setScheduleDay(schedule.day || 'daily');
          setScheduleHour(schedule.hour || '02');
          setScheduleEnabled(schedule.enabled || false);
        }
      } catch (error) {
        console.error('Error loading backup settings:', error);
      }
    };
    loadBackupSettings();
  }, []);

  const loadNotificationSettings = async () => {
    try {
      const settings = await getNotificationSettings();
      setNotifEnabled(settings.enabled || false);
      setNotifEmail(settings.recipientEmail || '');
      setNotifThreshold(String(settings.thresholdAmount || 0));
    } catch (error) {
      console.error('Error loading notification settings:', error);
    }
  };

  const handleSaveNotificationSettings = async () => {
    setIsSavingNotif(true);
    try {
      await updateNotificationSettings({
        enabled: notifEnabled,
        recipientEmail: notifEmail,
        thresholdAmount: parseFloat(notifThreshold) || 0
      });
      toast({ title: 'הצלחה!', description: 'הגדרות התראות נשמרו בהצלחה' });
    } catch (error) {
      toast({ title: 'שגיאה', description: 'לא ניתן לשמור הגדרות התראות', variant: 'destructive' });
    } finally {
      setIsSavingNotif(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!notifEmail) {
      toast({ title: 'שגיאה', description: 'נא להזין כתובת אימייל', variant: 'destructive' });
      return;
    }
    setIsSendingTest(true);
    try {
      const result = await sendTestEmail(notifEmail);
      toast({ title: 'הצלחה!', description: result.message });
    } catch (error) {
      toast({ title: 'שגיאה', description: error.response?.data?.detail || 'לא ניתן לשלוח אימייל בדיקה', variant: 'destructive' });
    } finally {
      setIsSendingTest(false);
    }
  };

  const handleConnectDrive = async () => {
    try {
      const result = await connectGoogleDrive();
      if (result.authorization_url) {
        window.location.href = result.authorization_url;
      }
    } catch (error) {
      toast({
        title: 'שגיאה',
        description: error.response?.data?.detail || 'לא ניתן להתחבר ל-Google Drive',
        variant: 'destructive'
      });
    }
  };

  const handleBackupToDrive = async () => {
    setIsBackingUpToDrive(true);
    try {
      const result = await backupToDrive();
      toast({
        title: 'הצלחה!',
        description: result.message,
      });
    } catch (error) {
      toast({
        title: 'שגיאה',
        description: error.response?.data?.detail || 'לא ניתן לגבות ל-Google Drive',
        variant: 'destructive'
      });
    } finally {
      setIsBackingUpToDrive(false);
    }
  };

  const handleSaveSchedule = async () => {
    setIsSavingSchedule(true);
    try {
      const result = await setBackupSchedule(scheduleDay, scheduleHour, scheduleEnabled);
      toast({
        title: 'הצלחה!',
        description: result.message,
      });
    } catch (error) {
      toast({
        title: 'שגיאה',
        description: 'לא ניתן לשמור את התזמון',
        variant: 'destructive'
      });
    } finally {
      setIsSavingSchedule(false);
    }
  };

  const handleRestoreBackup = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      toast({
        title: 'שגיאה',
        description: 'יש לבחור קובץ JSON בלבד',
        variant: 'destructive'
      });
      return;
    }

    if (!window.confirm('האם אתה בטוח? פעולה זו תחליף את כל הנתונים הקיימים בנתונים מהגיבוי!')) {
      event.target.value = '';
      return;
    }

    setIsRestoring(true);
    try {
      const result = await restoreBackup(file);
      toast({
        title: 'שחזור הושלם!',
        description: result.message,
      });
    } catch (error) {
      toast({
        title: 'שגיאה בשחזור',
        description: error.response?.data?.detail || 'לא ניתן לשחזר את הגיבוי',
        variant: 'destructive'
      });
    } finally {
      setIsRestoring(false);
      event.target.value = '';
    }
  };

  useEffect(() => {
    // Check if user is authenticated
    const isAuth = sessionStorage.getItem('managerAuth');
    if (!isAuth) {
      navigate('/manager-login');
    }
    
    // Load managers from localStorage
    const storedManagers = localStorage.getItem('managers');
    if (storedManagers) {
      setManagers(JSON.parse(storedManagers));
    }

    // Load suppliers and branches from backend
    loadSuppliers();
    loadBranches();
    loadExecutionSteps();
    loadPaymentMethods();
    loadBankAccounts();
    loadNotificationSettings();
    loadAllowedEmails();
  }, [navigate]);

  const loadAllowedEmails = async () => {
    try {
      const data = await getAllowedEmails();
      setAllowedEmails(data);
    } catch (error) {
      console.error('Error loading allowed emails:', error);
    }
  };

  // Recycle bin functions
  const loadRecycleBin = async () => {
    setIsLoadingRecycleBin(true);
    try {
      const data = await getRecycleBin(recycleBinFilter || undefined);
      setRecycleBinItems(data);
    } catch (error) {
      console.error('Error loading recycle bin:', error);
    } finally {
      setIsLoadingRecycleBin(false);
    }
  };

  const handleRestoreItem = async (itemId, itemLabel) => {
    try {
      await restoreFromRecycleBin(itemId);
      toast({ title: 'שוחזר!', description: `"${itemLabel}" שוחזר בהצלחה` });
      loadRecycleBin();
    } catch (error) {
      toast({ title: 'שגיאה', description: error.response?.data?.detail || 'לא ניתן לשחזר', variant: 'destructive' });
    }
  };

  const handlePermanentDelete = async (itemId, itemLabel) => {
    if (window.confirm(`למחוק לצמיתות את "${itemLabel}"? לא ניתן יהיה לשחזר.`)) {
      try {
        await permanentlyDeleteFromRecycleBin(itemId);
        toast({ title: 'נמחק', description: `"${itemLabel}" נמחק לצמיתות` });
        loadRecycleBin();
      } catch (error) {
        toast({ title: 'שגיאה', description: 'לא ניתן למחוק', variant: 'destructive' });
      }
    }
  };

  const handleEmptyRecycleBin = async () => {
    if (window.confirm('למחוק את כל הפריטים בסל המחזור לצמיתות? לא ניתן יהיה לשחזר!')) {
      try {
        const result = await emptyRecycleBin();
        toast({ title: 'הסל רוקן', description: result.message });
        setRecycleBinItems([]);
      } catch (error) {
        toast({ title: 'שגיאה', description: 'לא ניתן לרוקן את הסל', variant: 'destructive' });
      }
    }
  };

  useEffect(() => {
    if (recycleBinOpen) {
      loadRecycleBin();
    }
  }, [recycleBinOpen, recycleBinFilter]);

  // Sessions functions
  const loadSessions = async () => {
    setIsLoadingSessions(true);
    try {
      const data = await getLoginSessions();
      setActiveSessions(data);
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setIsLoadingSessions(false);
    }
  };

  const handleDisconnectSession = async (sessionId) => {
    if (sessionId === currentSessionId) {
      if (!window.confirm('אתה עומד לנתק את המכשיר הנוכחי. להמשיך?')) return;
    }
    try {
      await disconnectSession(sessionId);
      toast({ title: 'נותק', description: 'המכשיר נותק בהצלחה' });
      if (sessionId === currentSessionId) {
        localStorage.removeItem('sessionId');
      }
      loadSessions();
    } catch (error) {
      toast({ title: 'שגיאה', description: 'לא ניתן לנתק', variant: 'destructive' });
    }
  };

  const handleDisconnectAll = async () => {
    if (window.confirm('לנתק את כל המכשירים חוץ מהנוכחי?')) {
      try {
        const result = await disconnectAllSessions(currentSessionId);
        toast({ title: 'נותקו', description: result.message });
        loadSessions();
      } catch (error) {
        toast({ title: 'שגיאה', description: 'לא ניתן לנתק', variant: 'destructive' });
      }
    }
  };

  useEffect(() => {
    loadSessions();
    loadSuspiciousLogins();
    loadSecurityAlertSettings();
    loadGcalStatus();
  }, []);

  const loadGcalStatus = async () => {
    try {
      const status = await getGoogleCalendarStatus();
      setGcalStatus(status);
    } catch (e) {
      console.error('Error loading Google Calendar status:', e);
    }
  };

  const loadSecurityAlertSettings = async () => {
    try {
      const data = await getSecurityAlertSettings();
      setAlertEmail(data.alertEmail || '');
      setAlertPhone(data.alertPhone || '');
    } catch (error) {
      console.error('Error loading security alert settings:', error);
    }
  };

  const loadCompletedNotes = async () => {
    setIsLoadingNotes(true);
    try {
      const data = await getCompletedNotes();
      setCompletedNotes(data);
    } catch (error) {
      console.error('Error loading completed notes:', error);
    } finally {
      setIsLoadingNotes(false);
    }
  };

  const handleRestoreNote = async (noteId) => {
    try {
      await updateNote(noteId, { completed: false });
      toast({ title: 'שוחזר', description: 'ההערה הוחזרה לדף הבית' });
      loadCompletedNotes();
    } catch (error) {
      toast({ title: 'שגיאה', description: 'לא ניתן לשחזר', variant: 'destructive' });
    }
  };

  const handleDeleteCompletedNote = async (noteId) => {
    if (window.confirm('למחוק הערה זו לצמיתות?')) {
      try {
        await deleteNote(noteId);
        toast({ title: 'נמחק', description: 'ההערה נמחקה לצמיתות' });
        loadCompletedNotes();
      } catch (error) {
        toast({ title: 'שגיאה', description: 'לא ניתן למחוק', variant: 'destructive' });
      }
    }
  };

  const handleSaveAlertSettings = async () => {
    setIsSavingAlertSettings(true);
    try {
      await updateSecurityAlertSettings({ alertEmail: alertEmail, alertPhone: alertPhone });
      toast({ title: 'נשמר!', description: 'הגדרות התראות אבטחה נשמרו' });
    } catch (error) {
      toast({ title: 'שגיאה', description: 'לא ניתן לשמור', variant: 'destructive' });
    } finally {
      setIsSavingAlertSettings(false);
    }
  };

  const loadSuspiciousLogins = async () => {
    try {
      const data = await getSuspiciousLogins();
      setSuspiciousLogins(data.filter(l => !l.dismissed));
    } catch (error) {
      console.error('Error loading suspicious logins:', error);
    }
  };

  const handleDismissAlert = async (id) => {
    try {
      await dismissSuspiciousLogin(id);
      setSuspiciousLogins(prev => prev.filter(l => l.id !== id));
    } catch (error) {
      toast({ title: 'שגיאה', description: 'לא ניתן לסמן', variant: 'destructive' });
    }
  };

  const handleClearAllAlerts = async () => {
    try {
      await clearSuspiciousLogins();
      setSuspiciousLogins([]);
      toast({ title: 'נוקה', description: 'כל ההתראות נמחקו' });
    } catch (error) {
      toast({ title: 'שגיאה', description: 'לא ניתן לנקות', variant: 'destructive' });
    }
  };

  const handleAddAllowedEmail = async () => {
    if (!newAllowedEmail.trim()) {
      toast({ title: 'שגיאה', description: 'נא להזין אימייל', variant: 'destructive' });
      return;
    }
    setIsAddingEmail(true);
    try {
      await addAllowedEmail(newAllowedEmail.trim());
      toast({ title: 'נוסף', description: `${newAllowedEmail} נוסף לרשימה` });
      setNewAllowedEmail('');
      await loadAllowedEmails();
    } catch (error) {
      toast({ title: 'שגיאה', description: error.response?.data?.detail || 'לא ניתן להוסיף', variant: 'destructive' });
    } finally {
      setIsAddingEmail(false);
    }
  };

  const handleDeleteAllowedEmail = async (id, email) => {
    if (window.confirm(`להסיר את ${email} מהרשימה?`)) {
      try {
        await deleteAllowedEmail(id);
        toast({ title: 'הוסר', description: `${email} הוסר מהרשימה` });
        await loadAllowedEmails();
      } catch (error) {
        toast({ title: 'שגיאה', description: 'לא ניתן להסיר', variant: 'destructive' });
      }
    }
  };

  // F2 keyboard shortcut for customers list
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'F2') {
        e.preventDefault();
        navigate('/customers-list');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  const loadPaymentMethods = async () => {
    try {
      const data = await getPaymentMethods();
      setPaymentMethods(data);
    } catch (error) {
      console.error('Error loading payment methods:', error);
    }
  };

  const loadBankAccounts = async () => {
    try {
      const data = await getBankAccounts();
      setBankAccounts(data);
    } catch (error) {
      console.error('Error loading bank accounts:', error);
    }
  };

  const loadExecutionSteps = async () => {
    try {
      const data = await getExecutionSteps();
      setExecutionSteps(data);
    } catch (error) {
      console.error('Error loading execution steps:', error);
    }
  };

  const loadBranches = async () => {
    try {
      const data = await getBranches();
      setBranches(data);
    } catch (error) {
      console.error('Error loading branches:', error);
    }
  };

  const loadSuppliers = async () => {
    try {
      const data = await getSuppliers();
      setSuppliers(data);
    } catch (error) {
      console.error('Error loading suppliers:', error);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!currentPassword) {
      toast({ title: 'שגיאה', description: 'נא להזין סיסמה נוכחית', variant: 'destructive' });
      setIsSubmitting(false);
      return;
    }

    if (newPassword.length < 4) {
      toast({ title: 'שגיאה', description: 'הסיסמה החדשה חייבת להכיל לפחות 4 תווים', variant: 'destructive' });
      setIsSubmitting(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({ title: 'שגיאה', description: 'הסיסמאות אינן תואמות', variant: 'destructive' });
      setIsSubmitting(false);
      return;
    }

    try {
      // Verify current password by fetching managers
      const { getManagers, updateManagerPassword } = await import('../api');
      const managers = await getManagers();
      const loggedInName = localStorage.getItem('managerName') || 'aiman';
      const currentManager = managers.find(m => m.name === loggedInName);

      if (!currentManager || currentManager.password !== currentPassword) {
        toast({ title: 'שגיאה', description: 'הסיסמה הנוכחית שגויה', variant: 'destructive' });
        setIsSubmitting(false);
        return;
      }

      await updateManagerPassword(loggedInName, newPassword);
      toast({ title: 'הצלחה!', description: 'סיסמת הכניסה שונתה בהצלחה' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast({ title: 'שגיאה', description: 'לא ניתן לשנות את הסיסמה', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangeAdminPassword = (e) => {
    e.preventDefault();
    setIsSubmittingAdmin(true);

    // Get stored admin password or use default
    const storedAdminPassword = localStorage.getItem('adminPassword') || 'Admin1357';

    if (currentAdminPassword !== storedAdminPassword) {
      toast({
        title: 'שגיאה',
        description: 'סיסמת מנהל המנהלים הנוכחית שגויה',
        variant: 'destructive'
      });
      setIsSubmittingAdmin(false);
      return;
    }

    if (newAdminPassword.length < 6) {
      toast({
        title: 'שגיאה',
        description: 'הסיסמה החדשה חייבת להכיל לפחות 6 תווים',
        variant: 'destructive'
      });
      setIsSubmittingAdmin(false);
      return;
    }

    if (newAdminPassword !== confirmAdminPassword) {
      toast({
        title: 'שגיאה',
        description: 'הסיסמאות אינן תואמות',
        variant: 'destructive'
      });
      setIsSubmittingAdmin(false);
      return;
    }

    // Save new admin password
    localStorage.setItem('adminPassword', newAdminPassword);
    
    toast({
      title: 'הצלחה!',
      description: 'סיסמת מנהל המנהלים שונתה בהצלחה'
    });

    // Clear form
    setCurrentAdminPassword('');
    setNewAdminPassword('');
    setConfirmAdminPassword('');
    setIsSubmittingAdmin(false);
  };

  const handleChangeAimanPassword = (e) => {
    e.preventDefault();
    setIsSubmittingAiman(true);

    // Get stored AIMAN password or use default
    const storedAimanPassword = localStorage.getItem('aimanFolderPassword') || 'מנהל';

    if (currentAimanPassword !== storedAimanPassword) {
      toast({
        title: 'שגיאה',
        description: 'סיסמת תיקיית AIMAN הנוכחית שגויה',
        variant: 'destructive'
      });
      setIsSubmittingAiman(false);
      return;
    }

    if (newAimanPassword.length < 3) {
      toast({
        title: 'שגיאה',
        description: 'הסיסמה החדשה חייבת להכיל לפחות 3 תווים',
        variant: 'destructive'
      });
      setIsSubmittingAiman(false);
      return;
    }

    if (newAimanPassword !== confirmAimanPassword) {
      toast({
        title: 'שגיאה',
        description: 'הסיסמאות אינן תואמות',
        variant: 'destructive'
      });
      setIsSubmittingAiman(false);
      return;
    }

    // Save new AIMAN password
    localStorage.setItem('aimanFolderPassword', newAimanPassword);
    
    toast({
      title: 'הצלחה!',
      description: 'סיסמת תיקיית AIMAN שונתה בהצלחה'
    });

    // Clear form
    setCurrentAimanPassword('');
    setNewAimanPassword('');
    setConfirmAimanPassword('');
    setIsSubmittingAiman(false);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('managerAuth');
    navigate('/');
  };

  const handleAddManager = (e) => {
    e.preventDefault();
    setIsAddingManager(true);

    if (!newManagerName.trim()) {
      toast({
        title: 'שגיאה',
        description: 'נא להזין שם מנהל',
        variant: 'destructive'
      });
      setIsAddingManager(false);
      return;
    }

    if (!newManagerPassword || newManagerPassword.length < 4) {
      toast({
        title: 'שגיאה',
        description: 'נא להזין סיסמה (לפחות 4 תווים)',
        variant: 'destructive'
      });
      setIsAddingManager(false);
      return;
    }

    if (managers.find(m => m.name === newManagerName.trim())) {
      toast({
        title: 'שגיאה',
        description: 'מנהל זה כבר קיים במערכת',
        variant: 'destructive'
      });
      setIsAddingManager(false);
      return;
    }

    const newManager = {
      name: newManagerName.trim(),
      password: newManagerPassword
    };
    const updatedManagers = [...managers, newManager];
    setManagers(updatedManagers);
    localStorage.setItem('managers', JSON.stringify(updatedManagers));
    
    toast({
      title: 'הצלחה!',
      description: 'המנהל נוסף בהצלחה'
    });

    setNewManagerName('');
    setNewManagerPassword('');
    setIsAddingManager(false);
  };

  const handleDeleteManager = (managerName) => {
    if (window.confirm(`האם אתה בטוח שברצונך למחוק את ${managerName}?`)) {
      const updatedManagers = managers.filter(m => m.name !== managerName);
      setManagers(updatedManagers);
      localStorage.setItem('managers', JSON.stringify(updatedManagers));
      
      toast({
        title: 'הצלחה',
        description: 'המנהל נמחק בהצלחה'
      });
    }
  };

  const handleEditPassword = (manager) => {
    setEditingManager(manager.name);
    setEditPassword('');
  };

  const handleSavePassword = (managerName) => {
    if (!editPassword || editPassword.length < 4) {
      toast({
        title: 'שגיאה',
        description: 'נא להזין סיסמה (לפחות 4 תווים)',
        variant: 'destructive'
      });
      return;
    }

    const updatedManagers = managers.map(m => 
      m.name === managerName ? { ...m, password: editPassword } : m
    );
    setManagers(updatedManagers);
    localStorage.setItem('managers', JSON.stringify(updatedManagers));
    
    toast({
      title: 'הצלחה',
      description: 'הסיסמה עודכנה בהצלחה'
    });

    setEditingManager(null);
    setEditPassword('');
  };

  const handleCancelEdit = () => {
    setEditingManager(null);
    setEditPassword('');
  };
  // Supplier management functions
  const handleAddSupplier = async (e) => {
    e.preventDefault();
    setIsAddingSupplier(true);

    if (!newSupplierName.trim()) {
      toast({
        title: 'שגיאה',
        description: 'נא להזין שם ספק',
        variant: 'destructive'
      });
      setIsAddingSupplier(false);
      return;
    }

    try {
      await addSupplier({ name: newSupplierName.trim() });
      
      toast({
        title: 'הצלחה!',
        description: 'הספק נוסף בהצלחה'
      });

      setNewSupplierName('');
      await loadSuppliers(); // Reload suppliers from backend
    } catch (error) {
      toast({
        title: 'שגיאה',
        description: error.response?.data?.detail || 'שגיאה בהוספת ספק',
        variant: 'destructive'
      });
    }
    
    setIsAddingSupplier(false);
  };

  const handleDeleteSupplier = async (supplierId, supplierName) => {
    if (window.confirm(`האם אתה בטוח שברצונך למחוק את ${supplierName}?`)) {
      try {
        await deleteSupplier(supplierId);
        
        toast({
          title: 'הצלחה',
          description: 'הספק נמחק בהצלחה'
        });

        await loadSuppliers(); // Reload suppliers from backend
      } catch (error) {
        toast({
          title: 'שגיאה',
          description: 'שגיאה במחיקת ספק',
          variant: 'destructive'
        });
      }
    }
  };

  // Branch management functions
  const handleAddBranch = async (e) => {
    e.preventDefault();
    setIsAddingBranch(true);

    if (!newBranchName.trim()) {
      toast({
        title: 'שגיאה',
        description: 'נא להזין שם סניף',
        variant: 'destructive'
      });
      setIsAddingBranch(false);
      return;
    }

    try {
      await addBranch({ name: newBranchName.trim() });
      
      toast({
        title: 'הצלחה!',
        description: 'הסניף נוסף בהצלחה'
      });

      setNewBranchName('');
      await loadBranches();
    } catch (error) {
      toast({
        title: 'שגיאה',
        description: error.response?.data?.detail || 'שגיאה בהוספת סניף',
        variant: 'destructive'
      });
    }
    
    setIsAddingBranch(false);
  };

  const handleDeleteBranch = async (branchId, branchName) => {
    if (window.confirm(`האם אתה בטוח שברצונך למחוק את ${branchName}?`)) {
      try {
        await deleteBranch(branchId);
        
        toast({
          title: 'הצלחה',
          description: 'הסניף נמחק בהצלחה'
        });

        await loadBranches();
      } catch (error) {
        toast({
          title: 'שגיאה',
          description: 'שגיאה במחיקת סניף',
          variant: 'destructive'
        });
      }
    }
  };

  // Execution Steps handlers
  const handleAddStep = async () => {
    setIsAddingStep(true);

    if (!newStepName.trim()) {
      toast({
        title: 'שגיאה',
        description: 'נא להזין שם שלב',
        variant: 'destructive'
      });
      setIsAddingStep(false);
      return;
    }

    try {
      await addExecutionStep({ name: newStepName.trim() });
      
      toast({
        title: 'הצלחה!',
        description: 'השלב נוסף בהצלחה'
      });

      setNewStepName('');
      await loadExecutionSteps();
    } catch (error) {
      toast({
        title: 'שגיאה',
        description: 'שגיאה בהוספת שלב',
        variant: 'destructive'
      });
    } finally {
      setIsAddingStep(false);
    }
  };

  const handleDeleteStep = async (stepId, stepName) => {
    if (window.confirm(`האם אתה בטוח שברצונך למחוק את ${stepName}?`)) {
      try {
        await deleteExecutionStep(stepId);
        
        toast({
          title: 'הצלחה',
          description: 'השלב נמחק בהצלחה'
        });

        await loadExecutionSteps();
      } catch (error) {
        toast({
          title: 'שגיאה',
          description: 'שגיאה במחיקת שלב',
          variant: 'destructive'
        });
      }
    }
  };

  // Payment Methods handlers
  const handleAddPayment = async () => {
    setIsAddingPayment(true);

    if (!newPaymentName.trim()) {
      toast({
        title: 'שגיאה',
        description: 'נא להזין אמצעי תשלום',
        variant: 'destructive'
      });
      setIsAddingPayment(false);
      return;
    }

    try {
      await addPaymentMethod({ name: newPaymentName.trim() });
      
      toast({
        title: 'הצלחה!',
        description: 'אמצעי התשלום נוסף בהצלחה'
      });

      setNewPaymentName('');
      await loadPaymentMethods();
    } catch (error) {
      toast({
        title: 'שגיאה',
        description: 'שגיאה בהוספת אמצעי תשלום',
        variant: 'destructive'
      });
    } finally {
      setIsAddingPayment(false);
    }
  };

  const handleDeletePayment = async (paymentId, paymentName) => {
    if (window.confirm(`האם אתה בטוח שברצונך למחוק את ${paymentName}?`)) {
      try {
        await deletePaymentMethod(paymentId);
        
        toast({
          title: 'הצלחה',
          description: 'אמצעי התשלום נמחק בהצלחה'
        });

        await loadPaymentMethods();
      } catch (error) {
        toast({
          title: 'שגיאה',
          description: 'שגיאה במחיקת אמצעי תשלום',
          variant: 'destructive'
        });
      }
    }
  };

  const handleAddBankAccount = async () => {
    if (!newBankAccount.bankName || !newBankAccount.branch || !newBankAccount.accountNumber) {
      toast({
        title: 'שגיאה',
        description: 'נא למלא את כל השדות',
        variant: 'destructive'
      });
      return;
    }

    try {
      setIsAddingBankAccount(true);
      await addBankAccount(newBankAccount);
      toast({
        title: 'הצלחה',
        description: 'חשבון בנק נוסף בהצלחה'
      });
      setNewBankAccount({ bankName: '', branch: '', accountNumber: '' });
      await loadBankAccounts();
    } catch (error) {
      toast({
        title: 'שגיאה',
        description: 'שגיאה בהוספת חשבון בנק',
        variant: 'destructive'
      });
    } finally {
      setIsAddingBankAccount(false);
    }
  };

  const handleDeleteBankAccount = async (accountId, accountNumber) => {
    if (window.confirm(`האם אתה בטוח שברצונך למחוק את חשבון ${accountNumber}?`)) {
      try {
        await deleteBankAccount(accountId);
        toast({
          title: 'הצלחה',
          description: 'חשבון בנק נמחק בהצלחה'
        });

        await loadBankAccounts();
      } catch (error) {
        toast({
          title: 'שגיאה',
          description: 'שגיאה במחיקת חשבון בנק',
          variant: 'destructive'
        });
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4" dir="rtl">
      <div className="container mx-auto max-w-4xl">
        <Card className="shadow-2xl border-0">
          <CardHeader className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="w-8 h-8" />
                <CardTitle className="text-3xl font-bold">פאנל מנהל</CardTitle>
              </div>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-white text-white hover:bg-white/20"
              >
                התנתק
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <Tabs defaultValue="security" dir="rtl" className="w-full">
              <TabsList className="grid w-full grid-cols-5 mb-6 h-auto" data-testid="manager-tabs">
                <TabsTrigger value="security" className="flex items-center gap-2 py-3 text-sm font-semibold" data-testid="tab-security">
                  <Shield className="w-4 h-4" />
                  אבטחה
                </TabsTrigger>
                <TabsTrigger value="notifications" className="flex items-center gap-2 py-3 text-sm font-semibold" data-testid="tab-notifications">
                  <Bell className="w-4 h-4" />
                  התראות
                </TabsTrigger>
                <TabsTrigger value="management" className="flex items-center gap-2 py-3 text-sm font-semibold" data-testid="tab-management">
                  <Users className="w-4 h-4" />
                  ניהול
                </TabsTrigger>
                <TabsTrigger value="notes-history" className="flex items-center gap-2 py-3 text-sm font-semibold" data-testid="tab-notes-history" onClick={loadCompletedNotes}>
                  <StickyNote className="w-4 h-4" />
                  היסטוריית הערות
                </TabsTrigger>
                <TabsTrigger value="recycle" className="flex items-center gap-2 py-3 text-sm font-semibold" data-testid="tab-recycle">
                  <Trash className="w-4 h-4" />
                  סל מחזור
                </TabsTrigger>
              </TabsList>

              {/* ===== SECURITY TAB ===== */}
              <TabsContent value="security">
                <div className="space-y-6">
              {/* Change Login Password Section */}
              <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-6 rounded-lg border-2 border-amber-200" data-testid="change-password-section">
                <div className="flex items-center gap-3 mb-6">
                  <Key className="w-6 h-6 text-amber-600" />
                  <h2 className="text-2xl font-bold text-gray-900">שינוי סיסמת כניסה</h2>
                </div>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-base font-semibold text-gray-700">סיסמה נוכחית</Label>
                    <Input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="הזן סיסמה נוכחית"
                      className="text-base py-5 border-2 focus:border-amber-500 transition-colors"
                      data-testid="current-password-input"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-base font-semibold text-gray-700">סיסמה חדשה</Label>
                      <Input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="הזן סיסמה חדשה (לפחות 4 תווים)"
                        className="text-base py-5 border-2 focus:border-amber-500 transition-colors"
                        data-testid="new-password-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-base font-semibold text-gray-700">אישור סיסמה חדשה</Label>
                      <Input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="הזן שוב את הסיסמה החדשה"
                        className="text-base py-5 border-2 focus:border-amber-500 transition-colors"
                        data-testid="confirm-password-input"
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-5 bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-700 hover:to-yellow-700 text-white"
                    data-testid="change-password-submit-btn"
                  >
                    {isSubmitting ? 'משנה...' : 'שנה סיסמה'}
                  </Button>
                </form>
              </div>

              <VatSettings />

              <LockoutsPanel />

              {/* Suspicious Login Alerts */}
              {suspiciousLogins.length > 0 && (
                <div className="bg-gradient-to-br from-red-50 to-rose-50 p-5 rounded-lg border-2 border-red-300 animate-pulse-once" data-testid="suspicious-logins-section">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                      <h3 className="text-lg font-bold text-red-900">כניסות חשודות ({suspiciousLogins.length})</h3>
                    </div>
                    <Button
                      onClick={handleClearAllAlerts}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:bg-red-100 text-xs"
                      data-testid="clear-all-alerts-btn"
                    >
                      נקה הכל
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {suspiciousLogins.map((alert) => (
                      <div key={alert.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-red-200">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                            {alert.deviceType === 'נייד' ? (
                              <Smartphone className="w-4 h-4 text-red-600" />
                            ) : (
                              <Monitor className="w-4 h-4 text-red-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 text-sm">{alert.browser} - {alert.os}</p>
                            <p className="text-xs text-gray-500">
                              IP: {alert.ip} | {new Date(alert.timestamp).toLocaleDateString('he-IL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                        <Button
                          onClick={() => handleDismissAlert(alert.id)}
                          variant="ghost"
                          size="sm"
                          className="text-green-700 hover:bg-green-50"
                          data-testid={`dismiss-alert-${alert.id}`}
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Active Sessions Section */}
              <div className="bg-gradient-to-br from-cyan-50 to-teal-50 p-6 rounded-lg border-2 border-cyan-200" data-testid="active-sessions-section">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Monitor className="w-6 h-6 text-cyan-600" />
                    <h2 className="text-2xl font-bold text-gray-900">מכשירים מחוברים</h2>
                    <span className="text-sm bg-cyan-200 text-cyan-800 px-2.5 py-0.5 rounded-full font-medium">
                      {activeSessions.length}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={loadSessions}
                      variant="outline"
                      size="sm"
                      className="border-cyan-300 text-cyan-700 hover:bg-cyan-100"
                      data-testid="refresh-sessions-btn"
                    >
                      רענן
                    </Button>
                    {activeSessions.length > 1 && (
                      <Button
                        onClick={handleDisconnectAll}
                        variant="destructive"
                        size="sm"
                        data-testid="disconnect-all-btn"
                      >
                        <LogOut className="ml-1 w-3.5 h-3.5" />
                        נתק הכל
                      </Button>
                    )}
                  </div>
                </div>

                {isLoadingSessions ? (
                  <p className="text-center text-gray-500 py-4">טוען...</p>
                ) : activeSessions.length === 0 ? (
                  <div className="text-center py-6 text-gray-500 bg-white rounded-lg border">
                    <Monitor className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p>אין מכשירים מחוברים</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {activeSessions.map((session) => {
                      const isCurrentDevice = session.id === currentSessionId;
                      return (
                        <div
                          key={session.id}
                          className={`flex items-center justify-between p-4 rounded-lg border-2 transition-colors ${
                            isCurrentDevice
                              ? 'bg-cyan-100 border-cyan-400'
                              : 'bg-white border-gray-200 hover:border-gray-300'
                          }`}
                          data-testid={`session-${session.id}`}
                        >
                          <div className="flex items-center gap-3">
                            {session.deviceType === 'נייד' ? (
                              <Smartphone className="w-5 h-5 text-cyan-600" />
                            ) : (
                              <Monitor className="w-5 h-5 text-cyan-600" />
                            )}
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-900">
                                  {session.browser} - {session.os}
                                </span>
                                {isCurrentDevice && (
                                  <span className="text-[10px] bg-cyan-600 text-white px-2 py-0.5 rounded-full font-bold">
                                    מכשיר נוכחי
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                <span>IP: {session.ip}</span>
                                <span className="mx-2">|</span>
                                <span>
                                  התחבר: {new Date(session.loginAt).toLocaleDateString('he-IL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDisconnectSession(session.id)}
                            className="text-red-600 hover:bg-red-50 text-xs"
                            data-testid={`disconnect-session-${session.id}`}
                          >
                            <LogOut className="ml-1 w-3.5 h-3.5" />
                            נתק
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Security Alert Settings */}
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-6 rounded-lg border-2 border-orange-200" data-testid="security-alert-settings">
                <div className="flex items-center gap-3 mb-4">
                  <AlertTriangle className="w-6 h-6 text-orange-600" />
                  <h2 className="text-2xl font-bold text-gray-900">התראות כניסה חשודה</h2>
                </div>
                <p className="text-sm text-gray-600 mb-4 bg-white p-3 rounded-lg border">
                  כשמזוהה כניסה ממכשיר לא מוכר, תישלח התראה לאימייל ו/או SMS. השאר ריק כדי לבטל ערוץ.
                </p>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-base font-semibold text-gray-700 flex items-center gap-2">
                      <Mail className="w-4 h-4 text-orange-500" />
                      אימייל לקבלת התראות
                    </Label>
                    <Input
                      type="email"
                      value={alertEmail}
                      onChange={(e) => setAlertEmail(e.target.value)}
                      placeholder="your@email.com"
                      dir="ltr"
                      className="text-base py-5 border-2 focus:border-orange-500 transition-colors"
                      data-testid="alert-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-base font-semibold text-gray-700 flex items-center gap-2">
                      <Smartphone className="w-4 h-4 text-orange-500" />
                      מספר טלפון לקבלת SMS
                    </Label>
                    <Input
                      type="tel"
                      value={alertPhone}
                      onChange={(e) => setAlertPhone(e.target.value)}
                      placeholder="+972501234567"
                      dir="ltr"
                      className="text-base py-5 border-2 focus:border-orange-500 transition-colors"
                      data-testid="alert-phone-input"
                    />
                    <p className="text-xs text-gray-500">פורמט בינלאומי עם קידומת מדינה (למשל +972)</p>
                  </div>
                  <Button
                    onClick={handleSaveAlertSettings}
                    disabled={isSavingAlertSettings}
                    className="w-full py-5 bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white"
                    data-testid="save-alert-settings-btn"
                  >
                    {isSavingAlertSettings ? 'שומר...' : 'שמור הגדרות התראה'}
                  </Button>
                  <p className="text-xs text-gray-500 bg-white p-3 rounded-lg border">
                    <strong>שים לב:</strong> לשליחת אימייל צריך להגדיר RESEND_API_KEY, ולשליחת SMS צריך להגדיר פרטי Twilio בקובץ .env של השרת.
                  </p>
                </div>
              </div>
              </div>
              </TabsContent>

              {/* ===== NOTIFICATIONS TAB ===== */}
              <TabsContent value="notifications">
                <div className="space-y-6">
              {/* Notification Settings Section */}
              <div className="bg-gradient-to-br from-red-50 to-orange-50 p-6 rounded-lg border-2 border-red-200">
                <div className="flex items-center gap-3 mb-6">
                  <Bell className="w-6 h-6 text-red-600" />
                  <h2 className="text-2xl font-bold text-gray-900">הגדרות התראות חייבים</h2>
                </div>

                <div className="space-y-4">
                  {/* Enable/Disable */}
                  <div className="flex items-center gap-3 bg-white p-4 rounded-lg border">
                    <input
                      type="checkbox"
                      checked={notifEnabled}
                      onChange={(e) => setNotifEnabled(e.target.checked)}
                      className="w-5 h-5 accent-red-600"
                      data-testid="notif-enabled-checkbox"
                    />
                    <Label className="text-base font-semibold text-gray-700 cursor-pointer">
                      הפעל התראות אימייל על חייבים
                    </Label>
                  </div>

                  {/* Email Address */}
                  <div className="space-y-2">
                    <Label className="text-base font-semibold text-gray-700 flex items-center gap-2">
                      <Mail className="w-4 h-4 text-red-500" />
                      כתובת אימייל לקבלת התראות
                    </Label>
                    <Input
                      type="email"
                      value={notifEmail}
                      onChange={(e) => setNotifEmail(e.target.value)}
                      placeholder="example@email.com"
                      className="text-base py-5 border-2 focus:border-red-500 transition-colors"
                      dir="ltr"
                      data-testid="notif-email-input"
                    />
                  </div>

                  {/* Threshold Amount */}
                  <div className="space-y-2">
                    <Label className="text-base font-semibold text-gray-700">
                      סכום סף להתראה (₪) - 0 = כל סכום
                    </Label>
                    <Input
                      type="number"
                      value={notifThreshold}
                      onChange={(e) => setNotifThreshold(e.target.value)}
                      placeholder="0"
                      className="text-base py-5 border-2 focus:border-red-500 transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      data-testid="notif-threshold-input"
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <Button
                      onClick={handleSaveNotificationSettings}
                      disabled={isSavingNotif}
                      className="flex-1 py-5 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white"
                      data-testid="save-notif-settings-btn"
                    >
                      {isSavingNotif ? 'שומר...' : 'שמור הגדרות'}
                    </Button>
                    <Button
                      onClick={handleSendTestEmail}
                      disabled={isSendingTest || !notifEmail}
                      variant="outline"
                      className="py-5 border-2 border-red-500 text-red-600 hover:bg-red-50"
                      data-testid="send-test-email-btn"
                    >
                      <Send className="ml-2 w-4 h-4" />
                      {isSendingTest ? 'שולח...' : 'שלח אימייל בדיקה'}
                    </Button>
                  </div>

                  <p className="text-sm text-gray-500 bg-white p-3 rounded-lg border">
                    <strong>שים לב:</strong> כדי שהתראות אימייל יעבדו, צריך להגדיר מפתח Resend API בקובץ .env של השרת.
                    {!notifEnabled && <span className="text-red-500 font-semibold"> התראות כבויות כרגע.</span>}
                  </p>
                </div>
              </div>

              {/* Allowed Google Emails Section */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-lg border-2 border-blue-200">
                <div className="flex items-center gap-3 mb-6">
                  <Lock className="w-6 h-6 text-blue-600" />
                  <h2 className="text-2xl font-bold text-gray-900">הרשאות Google - חשבונות מורשים</h2>
                </div>

                <p className="text-sm text-gray-600 mb-4 bg-white p-3 rounded-lg border">
                  הוסף כתובות אימייל של חשבונות Google שמורשים להתחבר למערכת. אם הרשימה ריקה - כל חשבון Google יוכל להתחבר.
                </p>

                {/* Add Email Form */}
                <div className="flex gap-3 mb-4">
                  <Input
                    type="email"
                    value={newAllowedEmail}
                    onChange={(e) => setNewAllowedEmail(e.target.value)}
                    placeholder="user@gmail.com"
                    dir="ltr"
                    className="flex-1 text-base py-5 border-2 focus:border-blue-500 transition-colors"
                    onKeyDown={(e) => e.key === 'Enter' && handleAddAllowedEmail()}
                    data-testid="allowed-email-input"
                  />
                  <Button
                    onClick={handleAddAllowedEmail}
                    disabled={isAddingEmail}
                    className="px-6 py-5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
                    data-testid="add-allowed-email-btn"
                  >
                    {isAddingEmail ? 'מוסיף...' : 'הוסף'}
                  </Button>
                </div>

                {/* Emails List */}
                {allowedEmails.length === 0 ? (
                  <div className="text-center py-6 text-gray-500 bg-white rounded-lg border">
                    <Lock className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p>אין הגבלת אימייל - כל חשבון Google יכול להתחבר</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-blue-700 mb-2">
                      {allowedEmails.length} חשבונות מורשים:
                    </p>
                    {allowedEmails.map((item) => (
                      <div key={item.id} className="flex items-center justify-between bg-white p-3 rounded-lg border hover:border-blue-300 transition-colors">
                        <div className="flex items-center gap-3">
                          <Mail className="w-4 h-4 text-blue-500" />
                          <span className="font-medium text-gray-800" dir="ltr">{item.email}</span>
                        </div>
                        <button
                          onClick={() => handleDeleteAllowedEmail(item.id, item.email)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                          data-testid={`delete-email-${item.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              </div>
              </TabsContent>

              {/* ===== MANAGEMENT TAB ===== */}
              <TabsContent value="management">
                <div className="space-y-6">
              {/* Managers Management Section */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="flex items-center gap-3 mb-6">
                  <Users className="w-6 h-6 text-indigo-600" />
                  <h2 className="text-2xl font-bold text-gray-900">ניהול מנהלים</h2>
                </div>

                {/* Add Manager Form */}
                <form onSubmit={handleAddManager} className="space-y-4 mb-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      value={newManagerName}
                      onChange={(e) => setNewManagerName(e.target.value)}
                      placeholder="הזן שם מנהל חדש"
                      className="text-base py-5 border-2 focus:border-indigo-500 transition-colors"
                    />
                    <Input
                      type="password"
                      value={newManagerPassword}
                      onChange={(e) => setNewManagerPassword(e.target.value)}
                      placeholder="הזן סיסמה (לפחות 4 תווים)"
                      className="text-base py-5 border-2 focus:border-indigo-500 transition-colors"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isAddingManager}
                    className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
                  >
                    {isAddingManager ? 'מוסיף...' : 'הוסף מנהל'}
                  </Button>
                </form>

                {/* Managers List */}
                <div className="space-y-3">
                  <Label className="text-base font-semibold text-gray-700">רשימת מנהלים:</Label>
                  {managers.length === 0 ? (
                    <div className="text-sm text-gray-500 bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                      אין מנהלים במערכת. הוסף מנהל ראשון כדי להתחיל.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {managers.map((manager, index) => (
                        <div
                          key={index}
                          className="bg-white p-4 rounded-lg border-2 border-gray-200 hover:border-indigo-300 transition-colors"
                        >
                          {editingManager === manager.name ? (
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <span className="text-base font-medium text-gray-900">{manager.name}</span>
                                <div className="flex gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleCancelEdit()}
                                    className="text-gray-600 hover:text-gray-700"
                                  >
                                    ביטול
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleSavePassword(manager.name)}
                                    className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                  >
                                    שמור
                                  </Button>
                                </div>
                              </div>
                              <Input
                                type="password"
                                value={editPassword}
                                onChange={(e) => setEditPassword(e.target.value)}
                                placeholder="הזן סיסמה חדשה"
                                className="text-base border-2 focus:border-indigo-500"
                                autoFocus
                              />
                            </div>
                          ) : (
                            <div className="flex items-center justify-between">
                              <div className="space-y-1">
                                <span className="text-base font-medium text-gray-900 block">{manager.name}</span>
                                <span className="text-sm text-gray-500">סיסמה: {'•'.repeat(manager.password?.length || 4)}</span>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditPassword(manager)}
                                  className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteManager(manager.name)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Suppliers Management Section */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="flex items-center gap-3 mb-6">
                  <Package className="w-6 h-6 text-green-600" />
                  <h2 className="text-2xl font-bold text-gray-900">ניהול ספקים</h2>
                </div>

                {/* Add New Supplier Form */}
                <form onSubmit={handleAddSupplier} className="mb-6 space-y-4">
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <Input
                        type="text"
                        value={newSupplierName}
                        onChange={(e) => setNewSupplierName(e.target.value)}
                        placeholder="שם ספק חדש"
                        className="text-base py-5 border-2 focus:border-green-500 transition-colors"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isAddingSupplier}
                      className="px-8 py-5 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
                    >
                      {isAddingSupplier ? 'מוסיף...' : 'הוסף ספק'}
                    </Button>
                  </div>
                </form>

                {/* Suppliers List */}
                {suppliers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 bg-white rounded-lg border-2 border-dashed">
                    אין ספקים במערכת. הוסף ספק ראשון!
                  </div>
                ) : (
                  <div className="space-y-3">
                    {suppliers.map((supplier) => (
                      <div key={supplier.id} className="bg-white p-4 rounded-lg border-2 hover:border-green-300 transition-all">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Package className="w-5 h-5 text-green-600" />
                            <span className="font-semibold text-lg">{supplier.name}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteSupplier(supplier.id, supplier.name)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Branches Management Section */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="flex items-center gap-3 mb-6">
                  <Building2 className="w-6 h-6 text-blue-600" />
                  <h2 className="text-2xl font-bold text-gray-900">ניהול סניפים</h2>
                </div>

                {/* Add New Branch Form */}
                <form onSubmit={handleAddBranch} className="mb-6 space-y-4">
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <Input
                        type="text"
                        value={newBranchName}
                        onChange={(e) => setNewBranchName(e.target.value)}
                        placeholder="שם סניף חדש"
                        className="text-base py-5 border-2 focus:border-blue-500 transition-colors"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isAddingBranch}
                      className="px-8 py-5 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white"
                    >
                      {isAddingBranch ? 'מוסיף...' : 'הוסף סניף'}
                    </Button>
                  </div>
                </form>

                {/* Branches List */}
                {branches.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 bg-white rounded-lg border-2 border-dashed">
                    אין סניפים במערכת. הוסף סניף ראשון!
                  </div>
                ) : (
                  <div className="space-y-3">
                    {branches.map((branch) => (
                      <div key={branch.id} className="bg-white p-4 rounded-lg border-2 hover:border-blue-300 transition-all">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Building2 className="w-5 h-5 text-blue-600" />
                            <span className="font-semibold text-lg">{branch.name}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteBranch(branch.id, branch.name)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Info Section */}
              <div className="bg-blue-50 p-6 rounded-lg border-r-4 border-blue-500">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">מידע חשוב</h3>
                <ul className="text-blue-800 space-y-2">
                  <li>• הסיסמה הראשונית היא: Admin1357</li>
                  <li>• מומלץ לשנות את הסיסמה באופן קבוע</li>
                  <li>• הסיסמה החדשה חייבת להכיל לפחות 6 תווים</li>
                </ul>
              </div>

              {/* Execution Steps Management */}
              <div className="space-y-4">
                <div className="flex items-center gap-3 mb-4">
                  <ClipboardList className="w-7 h-7 text-purple-600" />
                  <h2 className="text-2xl font-bold text-gray-900">שלבי ניהול פרויקטים</h2>
                </div>

                <div className="flex gap-2">
                  <Input
                    value={newStepName}
                    onChange={(e) => setNewStepName(e.target.value)}
                    placeholder="הזן שם שלב חדש"
                    className="text-base border-2 border-purple-200 focus:border-purple-500"
                    onKeyPress={(e) => e.key === 'Enter' && handleAddStep()}
                  />
                  <Button
                    onClick={handleAddStep}
                    disabled={isAddingStep}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 whitespace-nowrap"
                  >
                    {isAddingStep ? 'מוסיף...' : 'הוסף שלב'}
                  </Button>
                </div>

                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {executionSteps.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      אין שלבי ביצוע. הוסף שלב ראשון למעלה.
                    </div>
                  ) : (
                    executionSteps.map((step) => (
                      <div
                        key={step.id}
                        className="flex items-center justify-between p-4 bg-purple-50 hover:bg-purple-100 rounded-lg border-2 border-purple-200 transition-colors"
                      >
                        <span className="font-semibold text-gray-800">{step.name}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteStep(step.id, step.name)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Payment Methods Management */}
              <div className="space-y-4">
                <div className="flex items-center gap-3 mb-4">
                  <CreditCard className="w-7 h-7 text-blue-600" />
                  <h2 className="text-2xl font-bold text-gray-900">אמצעי תשלום</h2>
                </div>

                <div className="flex gap-2">
                  <Input
                    value={newPaymentName}
                    onChange={(e) => setNewPaymentName(e.target.value)}
                    placeholder="הזן אמצעי תשלום חדש (לדוגמה: מזומן, שק, העברה בנקאית)"
                    className="text-base border-2 border-blue-200 focus:border-blue-500"
                    onKeyPress={(e) => e.key === 'Enter' && handleAddPayment()}
                  />
                  <Button
                    onClick={handleAddPayment}
                    disabled={isAddingPayment}
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 whitespace-nowrap"
                  >
                    {isAddingPayment ? 'מוסיף...' : 'הוסף'}
                  </Button>
                </div>

                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {paymentMethods.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      אין אמצעי תשלום. הוסף אמצעי תשלום ראשון למעלה.
                    </div>
                  ) : (
                    paymentMethods.map((method) => (
                      <div
                        key={method.id}
                        className="flex items-center justify-between p-4 bg-blue-50 hover:bg-blue-100 rounded-lg border-2 border-blue-200 transition-colors"
                      >
                        <span className="font-semibold text-gray-800">{method.name}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeletePayment(method.id, method.name)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Customers List Button */}
              {/* Bank Accounts Section */}
              <div className="space-y-4 p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border-2 border-green-200">
                <div className="flex items-center gap-2">
                  <Building2 className="w-6 h-6 text-green-600" />
                  <h2 className="text-2xl font-bold text-gray-900">חשבונות בנק</h2>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Input
                    value={newBankAccount.bankName}
                    onChange={(e) => setNewBankAccount({ ...newBankAccount, bankName: e.target.value })}
                    placeholder="שם הבנק"
                    className="text-lg"
                  />
                  <Input
                    value={newBankAccount.branch}
                    onChange={(e) => setNewBankAccount({ ...newBankAccount, branch: e.target.value })}
                    placeholder="מספר סניף"
                    className="text-lg"
                  />
                  <Input
                    value={newBankAccount.accountNumber}
                    onChange={(e) => setNewBankAccount({ ...newBankAccount, accountNumber: e.target.value })}
                    placeholder="מספר חשבון"
                    className="text-lg"
                  />
                </div>
                <Button
                  onClick={handleAddBankAccount}
                  disabled={isAddingBankAccount}
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                >
                  {isAddingBankAccount ? 'מוסיף...' : 'הוסף חשבון בנק'}
                </Button>
                <div className="space-y-2">
                  {bankAccounts.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">
                      אין חשבונות בנק. הוסף חשבון ראשון למעלה.
                    </p>
                  ) : (
                    bankAccounts.map((account) => (
                      <div
                        key={account.id}
                        className="flex items-center justify-between p-3 bg-white rounded-lg border border-green-200 hover:shadow-md transition-shadow"
                      >
                        <div>
                          <p className="font-semibold text-gray-800">{account.bankName}</p>
                          <p className="text-sm text-gray-600">סניף: {account.branch} | חשבון: {account.accountNumber}</p>
                        </div>
                        <Button
                          onClick={() => handleDeleteBankAccount(account.id, account.accountNumber)}
                          variant="destructive"
                          size="sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Button
                  onClick={() => navigate('/customers-list')}
                  className="w-full py-6 text-lg bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg transition-all"
                >
                  <Users className="ml-2 w-5 h-5" />
                  רשימת לקוחות
                </Button>
                <p className="text-sm text-center text-gray-500">
                  או לחץ <kbd className="px-2 py-1 bg-gray-200 rounded text-xs font-semibold">F2</kbd>
                </p>
                <Button
                  onClick={() => navigate('/price-list', { state: { workId: '__global__', workName: 'מחירון כללי' } })}
                  className="w-full py-6 text-lg bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white shadow-lg transition-all"
                  data-testid="manager-pricelist-btn"
                >
                  <Package className="ml-2 w-5 h-5" />
                  מחירון כללי
                </Button>
              </div>

              <div className="grid grid-cols-1 gap-4">
                {/* Backup Buttons */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border-2 border-dashed border-gray-300">
                  <div className="text-center mb-2 md:col-span-2">
                    <h3 className="text-lg font-bold text-gray-700">💾 גיבוי ושחזור נתונים</h3>
                    <p className="text-sm text-gray-500">הורד גיבוי, העלה גיבוי, או גבה ל-Google Drive</p>
                  </div>
                  
                  {/* Download Backup */}
                  <Button
                    onClick={() => {
                      try {
                        toast({
                          title: 'מוריד גיבוי...',
                          description: 'הגיבוי יורד למחשב שלך',
                        });
                        downloadBackup();
                      } catch (error) {
                        toast({
                          title: 'שגיאה',
                          description: 'לא ניתן להוריד גיבוי',
                          variant: 'destructive'
                        });
                      }
                    }}
                    className="w-full py-6 text-lg bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-lg transition-all"
                    data-testid="backup-download-button"
                  >
                    <Download className="ml-2 w-5 h-5" />
                    📥 הורד גיבוי
                  </Button>
                  
                  {/* Upload Backup */}
                  <div className="relative">
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleRestoreBackup}
                      ref={fileInputRef}
                      className="hidden"
                      data-testid="backup-file-input"
                    />
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isRestoring}
                      className="w-full py-6 text-lg bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg transition-all"
                      data-testid="backup-upload-button"
                    >
                      <Upload className="ml-2 w-5 h-5" />
                      {isRestoring ? '⏳ משחזר...' : '📤 העלה גיבוי'}
                    </Button>
                  </div>
                  
                  {/* Google Drive Backup */}
                  <div className="md:col-span-2">
                    {isDriveConnected ? (
                      <Button
                        onClick={handleBackupToDrive}
                        disabled={isBackingUpToDrive}
                        className="w-full py-6 text-lg bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white shadow-lg transition-all"
                        data-testid="backup-drive-button"
                      >
                        <Cloud className="ml-2 w-5 h-5" />
                        {isBackingUpToDrive ? '⏳ מגבה...' : '☁️ גיבוי ל-Google Drive'}
                      </Button>
                    ) : (
                      <Button
                        onClick={handleConnectDrive}
                        className="w-full py-6 text-lg bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white shadow-lg transition-all"
                        data-testid="connect-drive-button"
                      >
                        <Cloud className="ml-2 w-5 h-5" />
                        🔗 התחבר ל-Google Drive
                      </Button>
                    )}
                  </div>
                  
                  {/* Scheduled Backup */}
                  <div className="md:col-span-2 mt-4 p-4 bg-white rounded-lg border-2 border-purple-200">
                    <div className="flex items-center gap-2 mb-4">
                      <Clock className="w-5 h-5 text-purple-600" />
                      <h4 className="font-bold text-gray-700">גיבוי אוטומטי</h4>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                      <div>
                        <Label className="text-sm text-gray-600">יום:</Label>
                        <select
                          value={scheduleDay}
                          onChange={(e) => setScheduleDay(e.target.value)}
                          className="w-full p-2 border-2 rounded-md focus:border-purple-500"
                        >
                          <option value="daily">כל יום</option>
                          <option value="0">ראשון</option>
                          <option value="1">שני</option>
                          <option value="2">שלישי</option>
                          <option value="3">רביעי</option>
                          <option value="4">חמישי</option>
                          <option value="5">שישי</option>
                          <option value="6">שבת</option>
                        </select>
                      </div>
                      
                      <div>
                        <Label className="text-sm text-gray-600">שעה:</Label>
                        <select
                          value={scheduleHour}
                          onChange={(e) => setScheduleHour(e.target.value)}
                          className="w-full p-2 border-2 rounded-md focus:border-purple-500"
                        >
                          {[...Array(24)].map((_, i) => (
                            <option key={i} value={String(i).padStart(2, '0')}>
                              {String(i).padStart(2, '0')}:00
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <Button
                        onClick={handleSaveSchedule}
                        disabled={isSavingSchedule}
                        className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                      >
                        {isSavingSchedule ? '⏳ שומר...' : '💾 שמור תזמון'}
                      </Button>
                    </div>
                    
                    {scheduleEnabled && (
                      <p className="mt-2 text-sm text-green-600">
                        ✅ גיבוי אוטומטי פעיל
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Google Calendar Section */}
                <div className="bg-white p-6 rounded-lg shadow-md border-2 border-green-200 mb-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="currentColor"><path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zM5 8V6h14v2H5z"/></svg>
                    Google Calendar
                  </h3>
                  {gcalStatus.connected ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 bg-green-50 p-3 rounded-lg border border-green-200">
                        <div className="w-3 h-3 bg-green-500 rounded-full" />
                        <span className="text-sm font-semibold text-green-800">מחובר: {gcalStatus.email}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Button
                          disabled={gcalLoading}
                          onClick={async () => {
                            setGcalLoading(true);
                            try {
                              const today = new Date().toISOString().split('T')[0];
                              const result = await syncTasksToGoogleCalendar(today);
                              toast({ title: 'סונכרן!', description: `${result.synced} משימות סונכרנו ל-Google Calendar` });
                            } catch (e) {
                              toast({ title: 'שגיאה', description: 'לא ניתן לסנכרן', variant: 'destructive' });
                            } finally { setGcalLoading(false); }
                          }}
                          className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
                          data-testid="gcal-sync-push"
                        >
                          {gcalLoading ? 'מסנכרן...' : '📤 שלח משימות ליומן'}
                        </Button>
                        <Button
                          disabled={gcalLoading}
                          onClick={async () => {
                            setGcalLoading(true);
                            try {
                              const result = await pullEventsFromGoogleCalendar(30);
                              toast({ title: 'סונכרן!', description: `${result.created} אירועים חדשים יובאו מ-Google Calendar` });
                            } catch (e) {
                              toast({ title: 'שגיאה', description: 'לא ניתן למשוך אירועים', variant: 'destructive' });
                            } finally { setGcalLoading(false); }
                          }}
                          className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white"
                          data-testid="gcal-sync-pull"
                        >
                          {gcalLoading ? 'מסנכרן...' : '📥 משוך אירועים מיומן'}
                        </Button>
                      </div>
                      <Button
                        variant="outline"
                        className="w-full border-red-300 text-red-600 hover:bg-red-50"
                        onClick={async () => {
                          if (window.confirm('לנתק את Google Calendar?')) {
                            try {
                              await disconnectGoogleCalendar();
                              setGcalStatus({ connected: false, email: '' });
                              toast({ title: 'נותק', description: 'Google Calendar נותק בהצלחה' });
                            } catch (e) {
                              toast({ title: 'שגיאה', description: 'לא ניתק', variant: 'destructive' });
                            }
                          }
                        }}
                        data-testid="gcal-disconnect"
                      >
                        נתק Google Calendar
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-600">חבר את Google Calendar כדי לסנכרן משימות לאייפון ולקבל התראות.</p>
                      <Button
                        onClick={async () => {
                          try {
                            const { authorization_url } = await getGoogleCalendarAuthUrl();
                            window.location.href = authorization_url;
                          } catch (e) {
                            toast({ title: 'שגיאה', description: 'לא ניתן להתחבר', variant: 'destructive' });
                          }
                        }}
                        className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-4 text-lg"
                        data-testid="gcal-connect"
                      >
                        חבר Google Calendar
                      </Button>
                    </div>
                  )}
                </div>

                <Button
                  onClick={async () => {
                    try {
                      toast({
                        title: 'מסנכרן...',
                        description: 'מסנכרן את כל המשימות ל-Outlook Calendar',
                      });
                      const result = await syncAllDailyTasksToCalendar();
                      toast({
                        title: 'הצלחה!',
                        description: `סונכרנו ${result.synced_count} משימות ל-Outlook Calendar`,
                      });
                    } catch (error) {
                      toast({
                        title: 'שגיאה',
                        description: 'לא ניתן לסנכרן ל-Outlook Calendar',
                        variant: 'destructive'
                      });
                    }
                  }}
                  className="w-full py-6 text-lg bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg transition-all"
                >
                  📤 סנכרן משימות ל-Outlook
                </Button>
                
                <Button
                  onClick={async () => {
                    try {
                      toast({
                        title: 'מסנכרן...',
                        description: 'מסנכרן אירועים מ-Outlook Calendar',
                      });
                      const result = await syncDailyTasksFromCalendar();
                      toast({
                        title: 'הצלחה!',
                        description: `סונכרנו ${result.created_count} אירועים מ-Outlook Calendar`,
                      });
                    } catch (error) {
                      toast({
                        title: 'שגיאה',
                        description: 'לא ניתן לסנכרן מ-Outlook Calendar',
                        variant: 'destructive'
                      });
                    }
                  }}
                  className="w-full py-6 text-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg transition-all"
                >
                  📥 סנכרן אירועים מ-Outlook
                </Button>
              </div>
              </div>
              </TabsContent>

              {/* ===== NOTES HISTORY TAB ===== */}
              <TabsContent value="notes-history">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                      <StickyNote className="w-5 h-5 text-amber-500" />
                      היסטוריית הערות שהושלמו
                    </h2>
                    <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                      {completedNotes.length} הערות
                    </span>
                  </div>

                  {isLoadingNotes ? (
                    <p className="text-center text-gray-500 py-8">טוען...</p>
                  ) : completedNotes.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                      <StickyNote className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">אין הערות שהושלמו עדיין</p>
                      <p className="text-xs text-gray-400 mt-1">כשתסמן הערה עם V בדף הבית, היא תופיע כאן</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {completedNotes.map((note) => (
                        <div
                          key={note.id}
                          className="flex items-start gap-3 bg-white rounded-lg border p-4 hover:shadow-sm transition-shadow"
                          style={{ borderRightWidth: '4px', borderRightColor: note.color || '#FEF3C7' }}
                          data-testid={`completed-note-${note.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-600 line-through">{note.text}</p>
                            <div className="flex items-center gap-3 mt-2">
                              <span className="text-[11px] text-gray-400 flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                הושלם: {note.completedAt ? new Date(note.completedAt).toLocaleDateString('he-IL', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                              </span>
                              <span className="text-[11px] text-gray-400">
                                נוצר: {new Date(note.createdAt).toLocaleDateString('he-IL', { day: 'numeric', month: 'short' })}
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <button
                              onClick={() => handleRestoreNote(note.id)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="שחזר לדף הבית"
                              data-testid={`restore-note-${note.id}`}
                            >
                              <RotateCw className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteCompletedNote(note.id)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="מחק לצמיתות"
                              data-testid={`delete-completed-note-${note.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* ===== RECYCLE BIN TAB ===== */}
              <TabsContent value="recycle">
              {/* Recycle Bin Section */}
              <div className="bg-gradient-to-br from-gray-50 to-slate-100 p-6 rounded-lg border-2 border-gray-300" data-testid="recycle-bin-section">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Trash className="w-6 h-6 text-gray-600" />
                    <h2 className="text-2xl font-bold text-gray-900">סל מחזור</h2>
                  </div>
                  <Button
                    onClick={() => setRecycleBinOpen(!recycleBinOpen)}
                    variant="outline"
                    className="border-gray-400 text-gray-700 hover:bg-gray-200"
                    data-testid="toggle-recycle-bin-btn"
                  >
                    {recycleBinOpen ? 'סגור' : 'פתח סל מחזור'}
                  </Button>
                </div>

                {recycleBinOpen && (
                  <div className="space-y-4">
                    {/* Filter */}
                    <div className="flex gap-3 items-center flex-wrap">
                      <select
                        value={recycleBinFilter}
                        onChange={(e) => setRecycleBinFilter(e.target.value)}
                        className="p-2 border-2 rounded-lg focus:border-gray-500 text-sm"
                        data-testid="recycle-bin-filter"
                      >
                        <option value="">הכל</option>
                        <option value="daily_logs">רישום יומי</option>
                        <option value="deal_closures">סגירת עסקה</option>
                        <option value="service_calls">קריאות שירות</option>
                        <option value="projects">פרויקטים</option>
                        <option value="works">עבודות</option>
                        <option value="work_details">פירוט עבודה</option>
                        <option value="price_items">מחירון</option>
                        <option value="customers">לקוחות</option>
                        <option value="business_customers">לקוחות עסקיים</option>
                        <option value="private_customers">לקוחות פרטיים</option>
                        <option value="product_keys">מפתחות מוצר</option>
                        <option value="inventory">מלאי</option>
                        <option value="daily_tasks">משימות יומיות</option>
                        <option value="expenses">הוצאות</option>
                        <option value="incomes">הכנסות</option>
                        <option value="suppliers">ספקים</option>
                        <option value="branches">סניפים</option>
                        <option value="invoices">חשבוניות</option>
                        <option value="purchase_invoices">חשבוניות קנייה</option>
                        <option value="bank_accounts">חשבונות בנק</option>
                        <option value="managers">מנהלים</option>
                      </select>
                      {recycleBinItems.length > 0 && (
                        <Button
                          onClick={handleEmptyRecycleBin}
                          variant="destructive"
                          size="sm"
                          data-testid="empty-recycle-bin-btn"
                        >
                          <Trash2 className="ml-1 w-3.5 h-3.5" />
                          רוקן סל ({recycleBinItems.length})
                        </Button>
                      )}
                    </div>

                    {/* Items */}
                    {isLoadingRecycleBin ? (
                      <p className="text-center text-gray-500 py-6">טוען...</p>
                    ) : recycleBinItems.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 bg-white rounded-lg border">
                        <Trash className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                        <p>סל המחזור ריק</p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {recycleBinItems.map((item) => (
                          <div
                            key={item.id}
                            className="flex items-center justify-between bg-white p-3 rounded-lg border hover:border-gray-400 transition-colors"
                            data-testid={`recycle-item-${item.id}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 font-medium whitespace-nowrap">
                                  {item.itemType}
                                </span>
                                <span className="font-medium text-gray-900 truncate">
                                  {item.itemLabel || 'ללא שם'}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                נמחק: {new Date(item.deletedAt).toLocaleDateString('he-IL', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                            <div className="flex gap-1.5 mr-3">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRestoreItem(item.id, item.itemLabel)}
                                className="text-green-700 border-green-300 hover:bg-green-50 text-xs px-2.5"
                                data-testid={`restore-item-${item.id}`}
                              >
                                <RotateCcw className="ml-1 w-3.5 h-3.5" />
                                שחזר
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handlePermanentDelete(item.id, item.itemLabel)}
                                className="text-red-600 hover:bg-red-50 text-xs px-2"
                                data-testid={`perm-delete-item-${item.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              </TabsContent>
            </Tabs>

          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Manager;